import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, Printer } from "lucide-react";

const StatBox = ({ label, value, sub }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="eyebrow">{label}</div>
    <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Reports() {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [statements, setStatements] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    const now = new Date();
    setFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
    setTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10));
    api.get("/trips").then((r) => setTrips(r.data)).catch(() => {});
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/pay-statements").then((r) => setStatements(r.data)).catch(() => {});
  }, []);

  const driver = (id) => drivers.find((d) => d.driver_id === id);

  const inRange = useMemo(() => {
    return trips.filter((t) => (!from || t.load_date >= from) && (!to || t.load_date <= to));
  }, [trips, from, to]);
  const stmtInRange = useMemo(() => {
    return statements.filter((s) => (!from || (s.pay_date || s.created_at.slice(0, 10)) >= from) &&
                                    (!to || (s.pay_date || s.created_at.slice(0, 10)) <= to));
  }, [statements, from, to]);

  const totalMiles = inRange.reduce((s, t) => s + (parseFloat(t.paid_miles) || 0), 0);
  const totalGross = inRange.reduce((s, t) => s + (parseFloat(t.gross_amount) || 0), 0);
  const totalDriverPay = stmtInRange.reduce((s, x) => s + (x.totals?.gross || 0), 0);
  const totalDed = stmtInRange.reduce((s, x) => s + (x.totals?.deductions || 0), 0);
  const totalReimb = stmtInRange.reduce((s, x) => s + (x.totals?.reimbursements || 0), 0);
  const totalNet = stmtInRange.reduce((s, x) => s + (x.totals?.net || 0), 0);
  const avgPerMile = totalMiles ? totalDriverPay / totalMiles : 0;
  const costPerMile = totalMiles ? (totalDriverPay + totalReimb) / totalMiles : 0;

  // By driver — team trips use the trip-lead's rate so every team member gets
  // the same per-trip pay regardless of their individual rate.
  const byDriver = useMemo(() => {
    const map = {};
    inRange.forEach((t) => {
      const lead = driver(t.driver_ids[0]);
      t.driver_ids.forEach((did) => {
        const d = driver(did);
        if (!d) return;
        const key = did;
        if (!map[key]) map[key] = { driver: d, trips: 0, miles: 0, gross: 0, pay: 0 };
        const factor = t.is_team ? 0.5 : 1;
        const m = (parseFloat(t.paid_miles) || 0) * factor;
        const g = (parseFloat(t.gross_amount) || 0) * factor;
        map[key].trips += 1;
        map[key].miles += m;
        map[key].gross += g;
        const basis = t.is_team && lead ? lead : d;
        if (basis.pay_type === "per_mile") map[key].pay += m * basis.pay_rate;
        else map[key].pay += g * (basis.pay_rate / 100);
      });
    });
    return Object.values(map);
  }, [inRange, drivers]);

  const exportDriverCSV = () => {
    const rows = [["Driver", "Trips", "Miles", "Gross", "Driver Pay"]];
    byDriver.forEach((r) => rows.push([r.driver.name, r.trips, r.miles.toFixed(1), r.gross.toFixed(2), r.pay.toFixed(2)]));
    const csv = rows.map((r) => r.map((x) => `"${String(x)}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "report-by-driver.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Reports</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every filter recalculates instantly. Trip figures come from the trip log; deductions and reimbursements come from saved pay statements.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="reports-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="reports-to" />
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer size={14} className="mr-1" /> Print / PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
        <StatBox label="Total Miles" value={totalMiles.toLocaleString()} sub={`${inRange.length} trips`} />
        <StatBox label="Gross Revenue" value={`$${totalGross.toFixed(2)}`} />
        <StatBox label="Driver Pay" value={`$${totalDriverPay.toFixed(2)}`} />
        <StatBox label="Deductions" value={`$${totalDed.toFixed(2)}`} />
        <StatBox label="Reimbursements" value={`$${totalReimb.toFixed(2)}`} />
        <StatBox label="Net Pay" value={`$${totalNet.toFixed(2)}`} />
        <StatBox label="Avg Pay / Mile" value={`$${avgPerMile.toFixed(2)}/mi`} sub={`Cost: $${costPerMile.toFixed(2)}/mi`} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">By Driver</div>
            <div className="text-xs text-slate-500">{byDriver.length} drivers with activity</div>
          </div>
          <Button size="sm" variant="outline" onClick={exportDriverCSV} data-testid="report-csv">
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5 text-right">Trips</th>
                <th className="px-4 py-2.5 text-right">Miles</th>
                <th className="px-4 py-2.5 text-right">Gross</th>
                <th className="px-4 py-2.5 text-right">Driver Pay</th>
                <th className="px-4 py-2.5 text-right">Pay $/mi</th>
              </tr>
            </thead>
            <tbody>
              {byDriver.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  <div className="font-semibold">No activity in this period</div>
                  <div className="text-xs mt-1">Choose a wider date range, or log trips and pay runs first.</div>
                </td></tr>
              )}
              {byDriver.map((r) => (
                <tr key={r.driver.driver_id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-[11px] text-slate-500">{r.driver.display_id} </span>
                    {r.driver.name}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.trips}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.miles.toFixed(1)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">${r.gross.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold">${r.pay.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.miles ? `$${(r.pay / r.miles).toFixed(2)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
